import { useEffect } from 'react';
import {
    Order,
    Table,
    TableFilters,
    TableHeader,
    TableProps,
    TableProvider,
    TableState,
    useTableState,
} from '@optimus-web/core';
import { LocalizedDateFromUtc } from '@mews-commander/ui';
import { DataTestId } from '@mews-commander/enums';
import { DateTimeService, useTimezone } from '@mews/framework';
import { useProductCheckListQuery } from '../../queries/productCheckListQuery';
import { useProductCheckListFiltersContext } from '../../contexts/ProductCheckListFiltersContext';
import { productChecklistResultToProductCheckListData } from '../../utils/checkListData';
import { ProductCheckListDataRow } from '../../types';
import { AgeCategoriesContextProvider, useAgeCategoriesContext } from '../../contexts/AgeCategoriesContext';
import { ProductCheckListFilterValues } from '../../components/panels/ProductCheckListFilterFields/types';
import { useTableColumns } from './utils/useTableColumns';
import { useTableColumnsVisibility } from './utils/useTableColumnsVisibility';
import { DEFAULT_PAGE_SIZE } from './constants';
import { ProductCheckListTableColumn } from './types';

interface ProductCheckListTableProps {
    onLoadingChange: (newLoading: boolean) => void;
}

export const ProductCheckListTable: React.FC<ProductCheckListTableProps> = ({ onLoadingChange }) => {
    const { filters } = useProductCheckListFiltersContext();

    const [tableState, onTableStateChange] = useTableState<ProductCheckListDataRow>({
        pagination: { pageSize: DEFAULT_PAGE_SIZE },
        sort: {
            [ProductCheckListTableColumn.Resource]: { order: Order.Ascending, priority: 1 },
        },
    });

    const productCheckListQuery = useProductCheckListQuery(
        tableState,
        filters,
        productChecklistResultToProductCheckListData
    );

    useEffect(() => {
        onLoadingChange(productCheckListQuery.isFetching);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productCheckListQuery.isFetching]);

    return (
        <AgeCategoriesContextProvider ageCategories={productCheckListQuery.data?.ageCategories}>
            <TableWrapper
                {...{ filters, tableState, onTableStateChange, productCheckListQuery }}
                data={productCheckListQuery.data?.rows ?? []}
                amountOfRows={productCheckListQuery.data?.totalRowCount ?? 0}
                loading={productCheckListQuery.isFetching}
            />
        </AgeCategoriesContextProvider>
    );
};

interface TableWrapperProps extends Pick<TableProps<ProductCheckListDataRow>, 'loading' | 'data' | 'amountOfRows'> {
    filters: ProductCheckListFilterValues;
    tableState: TableState<ProductCheckListDataRow, TableFilters<ProductCheckListDataRow>>;
    onTableStateChange: React.Dispatch<
        React.SetStateAction<TableState<ProductCheckListDataRow, TableFilters<ProductCheckListDataRow>>>
    >;
}

const TableWrapper: React.FC<TableWrapperProps> = ({ filters, tableState, onTableStateChange, ...rest }) => {
    const { sortedAgeCategories } = useAgeCategoriesContext();
    const timezone = useTimezone();
    const dateTime = DateTimeService.fromUtcString(filters.date, timezone);
    const filterDate = DateTimeService.toDate(dateTime);

    const columns = useTableColumns(filterDate, sortedAgeCategories ?? []);

    const columnVisibility = useTableColumnsVisibility(rest.data, tableState, filters, sortedAgeCategories);

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={onTableStateChange} as="section">
            <TableHeader
                globalSearch
                title={
                    <LocalizedDateFromUtc utcString={filters.date} dataTestId={DataTestId.ProductCheckListDateTitle} />
                }
            />

            <Table
                uniqueRowId="id"
                columnVisibility={columnVisibility}
                fixedFirstColumn
                fixedHeader
                manualSorting
                {...rest}
            />
        </TableProvider>
    );
};
